import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, Text, StyleSheet, Modal, Pressable, ScrollView, TouchableOpacity } from "react-native";
import { COLORS, STRINGS } from "../../constants";
import EventStore from "../../storage/eventStore";
import Checkbox from "../Checkbox/Checkbox";

/**
 * FilterModal Component - Web Only
 *
 * A modal for web that provides filter controls with checkbox selection.
 * Uses EventStore for state management; direct updates without debouncing.
 */
const FilterModal = () => {
  const showFilter = EventStore((state) => state.showFilter);
  const tempFilter = EventStore((state) => state.tempFilter);
  const categories = EventStore((state) => state.categories);
  const loadingCategories = EventStore((state) => state.loadingCategories);
  const fetchCategories = EventStore((state) => state.fetchCategories);
  const closeFilterModal = EventStore((state) => state.closeFilterModal);
  const applyTempFilter = EventStore((state) => state.applyTempFilter);
  const setTempFilter = EventStore((state) => state.setTempFilter);
  const clearAllFilters = EventStore((state) => state.clearAllFilters);

  // Focus management refs
  const modalContentRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);
  
  // State to prevent double-submit on Apply button only
  const [isApplying, setIsApplying] = useState(false);

  /**
   * Focus management on modal open
   */
  useEffect(() => {
    if (showFilter && firstFocusableRef.current) {
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 100);
    }
  }, [showFilter]);

  /**
   * Handle modal close via overlay or ESC key
   */
  const handleModalClose = useCallback(() => {
    closeFilterModal();
  }, [closeFilterModal]);

  /**
   * Handle keyboard navigation for accessibility
   */
  const handleKeyDown = useCallback((event) => {
    if (!showFilter) return;

    // Close on Escape key
    if (event.key === 'Escape') {
      handleModalClose();
      return;
    }

    // Trap tab navigation within modal
    if (event.key === 'Tab') {
      const focusableElements = modalContentRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift+Tab - if focused on first element, move to last
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab - if focused on last element, move to first
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  }, [showFilter, handleModalClose]);

  /**
   * Add keyboard event listeners
   */
  useEffect(() => {
    if (showFilter) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showFilter, handleKeyDown]);

  /**
   * Fetch categories on component mount
   */
  useEffect(() => {
    if (categories.length === 0 && !loadingCategories) {
      fetchCategories();
    }
  }, [categories.length, loadingCategories, fetchCategories]);

  /**
   * Handle checkbox changes - read current tempFilter from store to avoid closure stale state
   */
  const handleCheckboxChange = useCallback((category) => {
    const current = EventStore.getState().tempFilter || [];
    const updated = current.includes(category) 
      ? current.filter((c) => c !== category) 
      : [...current, category];
    setTempFilter(updated);
  }, [setTempFilter]);

  /**
   * Handle Clear All button - reset all filters
   */
  const handleClearAll = useCallback(() => {
    clearAllFilters();
  }, [clearAllFilters]);

  /**
   * Handle Apply button - apply temporary filters and close modal
   * Only protection: prevent double-submit during async operation
   */
  const handleApply = useCallback(() => {
    if (isApplying) return;
    setIsApplying(true);
    
    applyTempFilter().finally(() => {
      setIsApplying(false);
    });
  }, [applyTempFilter, isApplying]);

  /**
   * Handle Cancel button - discard changes and close modal
   */
  const handleCancel = useCallback(() => {
    closeFilterModal();
  }, [closeFilterModal]);

  /**
   * Render category checkboxes
   */
  const renderCategories = () => {
    if (loadingCategories) {
      return (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{STRINGS.loadingCategories}</Text>
        </View>
      );
    }

    if (categories.length === 0) {
      return (
        <View style={styles.noCategoriesContainer}>
          <Text style={styles.noCategoriesText}>{STRINGS.noCategories}</Text>
        </View>
      );
    }

    return categories.map((category) => (
      <View key={category.name} style={styles.checkboxContainer}>
        <Checkbox
          label={category.name}
          value={tempFilter?.includes(category.name) || false}
          onChange={() => handleCheckboxChange(category.name)}
          color={COLORS.primary}
          uncheckedColor={COLORS.border}
        />
      </View>
    ));
  };

  return (
    <Modal
      visible={showFilter}
      animationType="fade"
      transparent={true}
      onRequestClose={handleModalClose}
      accessibilityViewIsModal={true}
    >
      <View 
        style={styles.modalOverlay} 
        onClick={handleModalClose}
      >
        <View style={styles.modalContentWrapper} pointerEvents="box-none">
          <View 
            onClick={(e) => e.stopPropagation()}
            style={styles.modalContent}
            ref={modalContentRef}
            accessibilityRole="dialog"
            accessibilityLabel="Filter options modal"
            accessibilityLiveRegion="polite"
          >
            
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text 
                style={styles.modalTitle}
                accessibilityRole="heading"
                accessibilityLevel={1}
              >
                {STRINGS.filterResults}
              </Text>
              <Pressable 
                ref={firstFocusableRef}
                onPress={handleClearAll} 
                style={styles.clearAllButton}
                disabled={isApplying}
                accessibilityRole="button"
                accessibilityLabel="Clear all filter selections"
                accessibilityHint="Removes all currently selected category filters"
              >
                <Text style={styles.clearAllText}>{STRINGS.clearAll}</Text>
              </Pressable>
            </View>

            {/* Modal Body */}
            <ScrollView 
              style={styles.modalBody} 
              contentContainerStyle={styles.modalBodyContent}
              showsVerticalScrollIndicator={false}
              accessibilityLabel="Filter categories list"
            >
              <View style={styles.categoriesContainer}>
                <Text 
                  style={styles.sectionHeading}
                  accessibilityRole="heading"
                  accessibilityLevel={2}
                >
                  {STRINGS.category}
                </Text>
                {renderCategories()}
              </View>
            </ScrollView>

            {/* Modal Footer */}
            <View style={styles.modalFooter}>
              <TouchableOpacity
                onPress={handleCancel}
                style={[styles.buttonStyle, styles.cancelButtonStyle]}
                disabled={isApplying}
                accessibilityLabel="Cancel filter changes"
                accessibilityHint="Discards any changes made to filters and closes the modal"
                accessibilityRole="button"
              >
                <Text style={styles.buttonText}>{STRINGS.cancel}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                ref={lastFocusableRef}
                onPress={handleApply}
                style={[styles.buttonStyle, styles.applyButtonStyle]}
                disabled={isApplying}
                accessibilityLabel="Apply filter changes"
                accessibilityHint="Saves the current filter selections and closes the modal"
                accessibilityRole="button"
              >
                <Text style={styles.buttonText}>{STRINGS.apply}</Text>
              </TouchableOpacity>
            </View>

          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContentWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  modalContent: {
    backgroundColor: COLORS.backgroundColor || "white",
    borderRadius: 12,
    maxHeight: "80%",
    width: "100%",
    maxWidth: 500,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F5F5F5",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: COLORS.textDarker || "#000",
  },
  clearAllButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  clearAllText: {
    fontSize: 14,
    color: COLORS.primary || "#007AFF",
    fontWeight: "500",
  },
  modalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  modalBodyContent: {
    paddingVertical: 16,
  },
  categoriesContainer: {
    width: "100%",
  },
  sectionHeading: {
    fontSize: 16,
    marginBottom: 16,
    fontWeight: "600",
    color: COLORS.textDarker || "#000",
  },
  checkboxContainer: {
    marginBottom: 12,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 20,
  },
  loadingText: {
    fontSize: 14,
    color: COLORS.textDark || "#666",
    marginLeft: 8,
  },
  noCategoriesContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 20,
  },
  noCategoriesText: {
    fontSize: 14,
    color: COLORS.textDark || "#666",
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    borderTopWidth: 1,
    borderTopColor: "#F5F5F5",
    gap: 12,
  },
  buttonStyle: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 20,
  },
  buttonText: {
    color: COLORS.textLight,
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
  },
  cancelButtonStyle: {
    opacity: 1,
  },
  applyButtonStyle: {
    opacity: 1,
  },
});

export default FilterModal;